import { Theme } from '@mui/material';
import { makeStyles } from '@mui/styles';
import React from 'react';
import Colors from '../../utils/color';

/**
 * PULSE METER TILE (HITL 3.0) — shared presentational chip for the Pulse strip's
 * time meters (Menu Board Time, Response time).
 *
 * Renders a value + label + a single thin progress bar that fills in proportion
 * to `elapsedMs / limitMs`, colour-coded by how close the elapsed time is to the
 * limit:
 *   0–50%   green (emerald)
 *   51–80%  amber
 *   81%+    red   (bittersweet) — over the limit it stays red (capped) so the
 *           breach reads clearly without a distracting blink.
 *
 * Compact + inline: two of these sit side by side in the Pulse strip (no card
 * chrome). Purely presentational: the owning wrapper computes `elapsedMs` (and
 * its tick cadence), decides visibility, and supplies the `formatValue`.
 */

const BAND = { green: 'green', yellow: 'yellow', red: 'red' } as const;
export type Band = (typeof BAND)[keyof typeof BAND];

// Exported so PulseDialChip (the 7.0 chip form of the same Response-time meter)
// escalates on exactly these thresholds instead of re-stating them.
export const bandForPct = (pct: number): Band => {
  if (pct <= 50) return BAND.green;
  if (pct <= 80) return BAND.yellow;
  return BAND.red; // 81–100% and anything over the limit
};

const bandColor = (band: Band): string => {
  switch (band) {
    case BAND.green:
      return Colors.emerald; // #30d158
    case BAND.yellow:
      // Warm amber (matches the Pulse mockup + the express-timer warning colour).
      return Colors.amber; // #ffc107
    default:
      return Colors.bittersweet; // #FC7373
  }
};

const useStyles = makeStyles((theme: Theme) => ({
  // A compact column, sized to its content — sits inline in the Pulse strip.
  tile: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(0.375),
    width: 'fit-content',
  },
  value: {
    fontSize: '1.02rem',
    fontWeight: 800,
    lineHeight: 1,
    fontVariantNumeric: 'tabular-nums',
    letterSpacing: '-0.01em',
  },
  label: {
    fontSize: '0.6rem',
    color: '#79808a',
    whiteSpace: 'nowrap',
    textTransform: 'uppercase',
    letterSpacing: '0.05em',
  },
  // Single thin track (replaces the old 6-segment row). A soft light track so the
  // coloured fill pops; the fill width is the elapsed fraction of the limit.
  track: {
    marginTop: theme.spacing(0.25),
    width: 74,
    height: 4,
    borderRadius: 3,
    backgroundColor: Colors.gallery,
    overflow: 'hidden',
  },
  fill: {
    display: 'block',
    height: '100%',
    borderRadius: 3,
    transition: 'width 40ms linear',
  },
}));

export interface PulseMeterTileProps {
  label: string;
  /** Elapsed time to display, in ms (or a plain count for count-style tiles). */
  elapsedMs: number;
  /** Threshold in ms; <= 0 disables colour escalation (stays green). */
  limitMs: number;
  /** Formats the numeral (e.g. "1:05" or "4"). */
  formatValue: (ms: number) => string;
  testId?: string;
  /**
   * Whether to render the progress track. Time meters (Menu Board Time, Response
   * time) keep it; count-style tiles with no threshold (Turn) pass `false`, which
   * also drops the green/amber/red colour band and shows the value in a neutral
   * colour. Defaults to true so existing tiles are unaffected.
   */
  showProgress?: boolean;
}

const PulseMeterTile = ({
  label,
  elapsedMs,
  limitMs,
  formatValue,
  testId,
  showProgress = true,
}: PulseMeterTileProps) => {
  const classes = useStyles();

  const pct = limitMs > 0 ? (elapsedMs / limitMs) * 100 : 0;
  const band = bandForPct(pct);
  // With no track there is no threshold to escalate against, so the value uses a
  // neutral colour rather than implying a green/amber/red status.
  const color = showProgress ? bandColor(band) : Colors.mineShaft;
  const fillPct = Math.max(0, Math.min(100, pct));

  return (
    <div
      className={classes.tile}
      data-testid={testId}
      data-band={showProgress ? band : undefined}
    >
      <div className={classes.value} style={{ color }}>
        {formatValue(elapsedMs)}
      </div>
      <div className={classes.label}>{label}</div>
      {showProgress && (
        <div className={classes.track} data-testid="pulse-meter-track">
          <span
            data-testid="pulse-meter-fill"
            className={classes.fill}
            style={{ width: `${fillPct}%`, backgroundColor: color }}
          />
        </div>
      )}
    </div>
  );
};

export default PulseMeterTile;
